import { MON_NAME, fmtD, ymAdd, ymOf, ymd, addDays } from './dates.ts';
import { amtCur, money, plural, rateF } from './format.ts';
import type { Data, Invoice, Service } from './types.ts';
import { mergeSettings } from './types.ts';
import { calcInv, r2, totals, type InvoiceCalc } from './vat.ts';

export interface InvRow {
  id: string;
  inv: Invoice;
  svc: Service | null;
  c: InvoiceCalc;
}

/** Nazwa dostawcy: z usługi (firma albo nazwa usługi), a dla faktury „Inne” – z faktury. Pusty, gdy nic nie wiadomo. */
export const vendorOf = (x: Pick<InvRow, 'inv' | 'svc'>): string => (x.svc ? x.svc.vendor || x.svc.name : x.inv.vendor || '').trim();

export function invoiceRows(data: Data): InvRow[] {
  return Object.entries(data.invoices).map(([id, inv]) => {
    const svc = data.services[inv.serviceId] || null;
    return { id, inv, svc, c: calcInv(inv, svc, data.rates) };
  });
}

export const rowsForMonth = (data: Data, ym: string) =>
  invoiceRows(data)
    .filter((x) => x.c.month === ym && !x.c.draft)
    .sort((a, b) => (a.c.tp?.date || '').localeCompare(b.c.tp?.date || ''));

/** Usługi miesięczne bez faktury (ani szkicu) w danym miesiącu, od settings.trackFrom do poprzedniego miesiąca. */
export function missingInvoices(data: Data, currentYM: string): { id: string; s: Service; m: string }[] {
  const st = mergeSettings(data.settings.main);
  const rows = invoiceRows(data);
  const out: { id: string; s: Service; m: string }[] = [];
  for (const [id, s] of Object.entries(data.services)) {
    if (s.active === false || s.cycle !== 'monthly') continue;
    let m = s.since && s.since > st.trackFrom ? s.since : st.trackFrom;
    for (; m < currentYM; m = ymAdd(m, 1)) {
      const has = rows.some((x) => x.inv.serviceId === id && (ymOf(x.inv.issueDate) === m || x.c.month === m));
      if (!has) out.push({ id, s, m });
    }
  }
  return out;
}

export function svcStats(data: Data, id: string, today: Date) {
  const rows = invoiceRows(data).filter((x) => x.inv.serviceId === id);
  const last = rows.map((x) => x.inv.issueDate || '').sort().pop() || '';
  const from = ymd(addDays(today, -365));
  const sum = rows.filter((x) => x.c.tp && x.c.tp.date >= from && x.c.net != null).reduce((a, x) => a + (x.c.net || 0), 0);
  return { n: rows.length, last, sum: Math.round(sum * 100) / 100 };
}

/** Zestawienie faktur zagranicznych dla biura (tekst do maila). */
export function packText(data: Data, m: string, folderLink?: string): string {
  const st = mergeSettings(data.settings.main);
  const rows = rowsForMonth(data, m);
  const tt = totals(rows.map((x) => x.c));
  const L = [`Faktury zagraniczne za ${MON_NAME[+m.slice(5) - 1]} ${m.slice(0, 4)}`];
  if (st.companyName) L.push(`${st.companyName}${st.nip ? ', NIP ' + st.nip : ''}`);
  L.push('');
  rows.forEach((x, i) => {
    const who = vendorOf(x) || '—';
    const head = `${i + 1}. ${who}${x.c.country ? ` (${x.c.country})` : ''}, faktura ${x.inv.number || 'bez numeru'}${x.inv.issueDate ? ` z ${fmtD(x.inv.issueDate)}` : ''}`;
    const amount = amtCur(x.inv.amount ?? null, x.c.cur);
    L.push(x.c.cur === 'PLN'
      ? `${head}: ${amount}`
      : `${head}: ${amount}, kurs NBP ${rateF(x.c.rate)} z ${fmtD(x.c.rd)}${x.c.rateTable ? ` (tabela ${x.c.rateTable})` : ''}, ${money(x.c.net)} netto`);
  });
  if (!rows.length) L.push('Brak faktur w tym miesiącu.');
  L.push('', `Razem netto: ${money(r2(tt.k27 + tt.k29))} (${rows.length} ${plural(rows.length, 'faktura', 'faktury', 'faktur')})`);
  if (folderLink) L.push(`Pliki faktur: ${folderLink}`);
  if (tt.missing) L.push(`Uwaga: ${tt.missing} ${plural(tt.missing, 'faktura', 'faktury', 'faktur')} bez kursu NBP, nie wliczone do sumy.`);
  return L.join('\n');
}

/** CSV dla biura: średniki, przecinek dziesiętny, BOM – otwiera się poprawnie w polskim Excelu. */
export function packCSV(data: Data, m: string): string {
  const rows = rowsForMonth(data, m);
  const n = (x: number | string | null | undefined) => (x == null || x === '' ? '' : String(x).replace('.', ','));
  const q = (s: unknown) => `"${String(s ?? '').replace(/"/g, '""')}"`;
  const H = ['Dostawca', 'Kraj', 'Nr faktury', 'Data wystawienia', 'Data zapłaty', 'Okres od', 'Okres do',
    'Waluta', 'Kwota netto', 'Kurs NBP', 'Data kursu', 'Tabela NBP', 'Netto PLN', 'Plik'];
  const lines = [H.map(q).join(';')];
  for (const x of rows) {
    const i = x.inv, c = x.c;
    lines.push([vendorOf(x), c.country, i.number, i.issueDate, i.paidDate, i.periodFrom, i.periodTo,
      c.cur, n(i.amount ?? null), c.cur === 'PLN' ? '' : n(c.rate), c.cur === 'PLN' ? '' : c.rd, c.rateTable, n(c.net), i.fileLink || ''].map(q).join(';'));
  }
  return '\ufeff' + lines.join('\r\n');
}
